/**
 * ScrapeKing Design System v2 - Demo Dashboard
 * Complete showcase of the "Data Extraction Terminal" aesthetic
 */

import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Import all components
import AppLayoutV2 from '../layouts/AppLayoutV2';
import { Button, IconButton } from '../components/Button';
import { TextInput, SearchInput, Select, Textarea } from '../components/Input';
import { Card, StatCard, StatusIndicator, DataTable } from '../components/Card';
import { PageHeader, SectionHeader } from '../components/PageHeader';
import { Badge, StatusBadge, CountBadge } from '../components/Badge';

// ═══════════════════════════════════════════════════════════════
// STYLED COMPONENTS FOR DEMO LAYOUT
// ═══════════════════════════════════════════════════════════════

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--sk-space-5);
  margin-bottom: var(--sk-space-8);

  > * {
    animation: ${fadeIn} 0.4s ease backwards;
  }

  > *:nth-child(1) { animation-delay: 0ms; }
  > *:nth-child(2) { animation-delay: 50ms; }
  > *:nth-child(3) { animation-delay: 100ms; }
  > *:nth-child(4) { animation-delay: 150ms; }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--sk-space-6);
  margin-bottom: var(--sk-space-8);

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const Spacer = styled.div<{ $size?: number }>`
  height: ${props => props.$size || 32}px;
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sk-space-3);
  flex-wrap: wrap;
`;

const ComponentShowcase = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sk-space-4);
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--sk-space-4);
`;

const BadgeShowcase = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sk-space-2);
`;

// ═══════════════════════════════════════════════════════════════
// DEMO ICONS
// ═══════════════════════════════════════════════════════════════

const IconDatabase = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const IconActivity = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" />
  </svg>
);

const IconUsers = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconZap = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13,2 3,14 12,14 11,22 21,10 12,10 13,2" />
  </svg>
);

const IconPlus = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconDownload = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7,10 12,15 17,10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const IconRefresh = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23,4 23,10 17,10" />
    <polyline points="1,20 1,14 7,14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════
// DEMO DATA
// ═══════════════════════════════════════════════════════════════

const mockCampaigns = [
  { id: 1, name: 'LinkedIn B2B Leads', type: 'Scraper', status: 'running', leads: 12450, lastRun: '2 min ago' },
  { id: 2, name: 'E-commerce Product Data', type: 'Visual', status: 'completed', leads: 8920, lastRun: '1 hour ago' },
  { id: 3, name: 'Real Estate Listings', type: 'Scraper', status: 'paused', leads: 3200, lastRun: '3 days ago' },
  { id: 4, name: 'Job Board Aggregator', type: 'Scraper', status: 'failed', leads: 0, lastRun: '5 min ago' },
  { id: 5, name: 'News Article Collector', type: 'Visual', status: 'pending', leads: 0, lastRun: 'Never' },
];

// ═══════════════════════════════════════════════════════════════
// DEMO COMPONENT
// ═══════════════════════════════════════════════════════════════

const DemoDashboard: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLoadingDemo = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 2000);
  };

  // Header actions
  const headerActions = (
    <FlexRow>
      <Button variant="secondary" icon={<IconDownload />}>
        Export
      </Button>
      <Button variant="primary" icon={<IconPlus />}>
        New Campaign
      </Button>
    </FlexRow>
  );

  return (
    <AppLayoutV2 headerActions={
      <FlexRow>
        <StatusIndicator status="active" text="System Online" />
        <Button variant="ghost" size="sm">
          <IconRefresh />
        </Button>
      </FlexRow>
    }>
      {/* Page Header */}
      <PageHeader
        title="Data Extraction Dashboard"
        subtitle="Monitor and manage your web scraping campaigns. Track leads, analyze performance, and optimize your data collection."
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Dashboard' },
        ]}
        status="active"
        statusText="Live"
        actions={headerActions}
        commandHint="scrapeking --dashboard --live"
      />

      {/* Stats Grid */}
      <StatsGrid>
        <StatCard
          label="Total Leads"
          value="24,570"
          icon={<IconDatabase />}
          change={12.5}
          variant="cyan"
        />
        <StatCard
          label="Active Campaigns"
          value="8"
          icon={<IconActivity />}
          change={-2.3}
          variant="green"
        />
        <StatCard
          label="Unique Contacts"
          value="18,234"
          icon={<IconUsers />}
          change={8.1}
          variant="amber"
        />
        <StatCard
          label="API Calls Today"
          value="1.2M"
          icon={<IconZap />}
          change={45.2}
          variant="cyan"
        />
      </StatsGrid>

      {/* Main Content Grid */}
      <TwoColumnGrid>
        {/* Campaigns Table */}
        <div>
          <SectionHeader
            title="Recent Campaigns"
            description="Your most recent scraping campaigns and their current status"
            badge="5 Active"
            actions={
              <SearchInput
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onClear={() => setSearchValue('')}
                placeholder="Search campaigns..."
              />
            }
          />
          <DataTable.Card>
            <DataTable.Wrapper>
              <DataTable.Table>
                <DataTable.Head>
                  <tr>
                    <DataTable.HeadCell>Campaign</DataTable.HeadCell>
                    <DataTable.HeadCell>Type</DataTable.HeadCell>
                    <DataTable.HeadCell>Status</DataTable.HeadCell>
                    <DataTable.HeadCell>Leads</DataTable.HeadCell>
                    <DataTable.HeadCell>Last Run</DataTable.HeadCell>
                  </tr>
                </DataTable.Head>
                <DataTable.Body>
                  {mockCampaigns.map((campaign) => (
                    <DataTable.Row key={campaign.id}>
                      <DataTable.Cell>
                        <strong style={{ color: 'var(--sk-text-primary)' }}>{campaign.name}</strong>
                      </DataTable.Cell>
                      <DataTable.Cell>
                        <Badge variant={campaign.type === 'Visual' ? 'purple' : 'cyan'} size="sm">
                          {campaign.type}
                        </Badge>
                      </DataTable.Cell>
                      <DataTable.Cell>
                        <StatusBadge status={campaign.status as any} size="sm" />
                      </DataTable.Cell>
                      <DataTable.Cell>
                        <span style={{ fontFamily: 'var(--sk-font-mono)' }}>
                          {campaign.leads.toLocaleString()}
                        </span>
                      </DataTable.Cell>
                      <DataTable.Cell>
                        <span style={{ color: 'var(--sk-text-tertiary)' }}>{campaign.lastRun}</span>
                      </DataTable.Cell>
                    </DataTable.Row>
                  ))}
                </DataTable.Body>
              </DataTable.Table>
            </DataTable.Wrapper>
          </DataTable.Card>
        </div>

        {/* Sidebar Content */}
        <div>
          <SectionHeader title="Quick Actions" />
          <Card variant="default">
            <Card.Body>
              <ComponentShowcase>
                <Button variant="primary" fullWidth icon={<IconPlus />}>
                  Create New Scraper
                </Button>
                <Button variant="secondary" fullWidth>
                  Import Leads
                </Button>
                <Button variant="ghost" fullWidth>
                  View Documentation
                </Button>
                <Button
                  variant="primary"
                  fullWidth
                  loading={loading}
                  onClick={handleLoadingDemo}
                >
                  {loading ? 'Processing...' : 'Test Loading State'}
                </Button>
              </ComponentShowcase>
            </Card.Body>
          </Card>

          <Spacer $size={24} />

          <SectionHeader title="System Status" />
          <Card variant="bordered">
            <Card.Body>
              <ComponentShowcase>
                <StatusIndicator status="active" text="API Server: Online" />
                <StatusIndicator status="active" text="Database: Connected" />
                <StatusIndicator status="warning" text="Queue: High Load" />
                <StatusIndicator status="idle" text="Scheduler: Idle" />
              </ComponentShowcase>
            </Card.Body>
          </Card>
        </div>
      </TwoColumnGrid>

      {/* Component Showcase Section */}
      <Spacer $size={48} />
      <SectionHeader
        title="UI Component Showcase"
        description="Demonstration of all design system components"
        badge="v2.0"
      />

      <Card variant="elevated">
        <Card.Header>
          <Card.Title>Button Variants</Card.Title>
        </Card.Header>
        <Card.Body>
          <ComponentShowcase>
            <FlexRow>
              <Button variant="primary">Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="danger">Danger</Button>
              <Button variant="success">Success</Button>
            </FlexRow>
            <FlexRow>
              <Button variant="primary" size="sm">Small</Button>
              <Button variant="primary" size="md">Medium</Button>
              <Button variant="primary" size="lg">Large</Button>
            </FlexRow>
            <FlexRow>
              <Button variant="primary" icon={<IconPlus />}>With Icon</Button>
              <Button variant="primary" icon={<IconDownload />} iconPosition="right">Icon Right</Button>
              <Button variant="primary" disabled>Disabled</Button>
            </FlexRow>
          </ComponentShowcase>
        </Card.Body>
      </Card>

      <Spacer $size={24} />

      <Card variant="elevated">
        <Card.Header>
          <Card.Title>Input Components</Card.Title>
        </Card.Header>
        <Card.Body>
          <InputGrid>
            <TextInput
              label="Campaign Name"
              placeholder="Enter campaign name..."
              helperText="Choose a descriptive name for your scraper"
            />
            <TextInput
              label="Target URL"
              placeholder="https://example.com"
              error="Invalid URL format"
            />
            <Select
              label="Campaign Type"
              options={[
                { value: 'scraper', label: 'Web Scraper' },
                { value: 'visual', label: 'Visual Builder' },
                { value: 'api', label: 'API Connector' },
              ]}
            />
            <TextInput
              label="Search (with icon)"
              placeholder="Search..."
              icon={<IconDatabase />}
            />
          </InputGrid>
        </Card.Body>
      </Card>

      <Spacer $size={24} />

      <Card variant="elevated">
        <Card.Header>
          <Card.Title>Badges & Status Indicators</Card.Title>
        </Card.Header>
        <Card.Body>
          <ComponentShowcase>
            <div>
              <SectionHeader title="Status Badges" />
              <BadgeShowcase>
                <StatusBadge status="running" />
                <StatusBadge status="completed" />
                <StatusBadge status="pending" />
                <StatusBadge status="failed" />
                <StatusBadge status="paused" />
              </BadgeShowcase>
            </div>
            <div>
              <SectionHeader title="Color Variants" />
              <BadgeShowcase>
                <Badge variant="default">Default</Badge>
                <Badge variant="cyan">Cyan</Badge>
                <Badge variant="green">Green</Badge>
                <Badge variant="amber">Amber</Badge>
                <Badge variant="red">Red</Badge>
                <Badge variant="purple">Purple</Badge>
              </BadgeShowcase>
            </div>
            <div>
              <SectionHeader title="Count Badges" />
              <BadgeShowcase>
                <CountBadge count={5} variant="cyan" />
                <CountBadge count={42} variant="green" />
                <CountBadge count={100} max={99} variant="red" />
              </BadgeShowcase>
            </div>
          </ComponentShowcase>
        </Card.Body>
      </Card>

      <Spacer $size={48} />
    </AppLayoutV2>
  );
};

export default DemoDashboard;
